import { useState, useEffect, useRef } from "react";
import { API } from "../App";
import axios from "axios";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Trophy, Users, Target, Award, Star, TrendingUp, Calendar, Swords, Search, ChevronRight, ArrowUpDown } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";

const StatRow = ({ rank, player, statType }) => {
  if (!player || !player.name) return null;
  return (
  <div 
    className={`flex items-center gap-4 p-3 rounded-lg ${rank <= 3 ? 'bg-gradient-to-r from-[#7C2C3B]/10 to-transparent' : 'hover:bg-slate-50'} transition-colors`}
    data-testid={`stat-row-${rank}`}
  >
    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
      rank === 1 ? 'bg-[#F8D742] text-[#0F172A]' :
      rank === 2 ? 'bg-slate-300 text-[#0F172A]' :
      rank === 3 ? 'bg-amber-600 text-white' :
      'bg-slate-100 text-slate-600'
    }`}>
      {rank}
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-semibold text-[#0F172A] truncate" style={{ fontFamily: 'Barlow Condensed' }}>
        {player.name.toUpperCase()}
      </div>
      <div className="text-xs text-slate-500">
        {player.positions?.join(', ') || 'Player'} • {player.years_active || 'Historical'}
      </div>
    </div>
    <div className="text-right">
      <div className={`text-2xl font-bold ${statType === 'goals' ? 'text-[#7C2C3B]' : 'text-[#2BAEE5]'}`} style={{ fontFamily: 'Barlow Condensed' }}>
        {statType === 'goals' ? player.goals : player.appearances}
      </div>
      <div className="text-xs text-slate-500">
        {statType === 'goals' ? 'goals' : 'apps'}
      </div>
    </div>
    {statType === 'goals' && (
      <div className="text-right w-16">
        <div className="text-sm text-slate-600">{player.appearances}</div>
        <div className="text-xs text-slate-400">apps</div>
      </div>
    )}
    {statType === 'appearances' && (
      <div className="text-right w-16">
        <div className="text-sm text-slate-600">{player.goals}</div>
        <div className="text-xs text-slate-400">goals</div>
      </div>
    )}
  </div>
);
};

const RatingRow = ({ rank, player, showMinutes = false, showGames = false }) => {
  const getRatingColor = (rating) => {
    if (rating === null || rating === undefined || rating === '-') return 'bg-slate-400';
    if (rating >= 8.0) return 'bg-green-500';
    if (rating >= 7.0) return 'bg-lime-500';
    if (rating >= 6.0) return 'bg-yellow-500';
    if (rating >= 5.0) return 'bg-orange-500';
    return 'bg-red-500';
  };

  if (!player || !player.name) return null;

  return (
    <div 
      className={`flex items-center gap-3 p-3 rounded-lg ${rank <= 3 ? 'bg-gradient-to-r from-[#F8D742]/10 to-transparent' : 'hover:bg-slate-50'} transition-colors ${player.motm ? 'ring-2 ring-[#F8D742]' : ''} ${player.red_card ? 'bg-red-50' : ''}`}
      data-testid={`rating-row-${rank}`}
    >
      <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs ${
        rank === 1 ? 'bg-[#F8D742] text-[#0F172A]' :
        rank === 2 ? 'bg-slate-300 text-[#0F172A]' :
        rank === 3 ? 'bg-amber-600 text-white' :
        'bg-slate-100 text-slate-600'
      }`}>
        {rank}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[#0F172A] text-sm truncate flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
          {player.name.toUpperCase()}
          {player.motm && <span className="text-[10px] bg-[#F8D742] text-[#0F172A] px-1 rounded">MOTM</span>}
          {player.red_card && <span className="text-[10px] bg-red-600 text-white px-1 rounded">RED</span>}
        </div>
        <div className="text-xs text-slate-500">
          {player.position || 'Player'}
          {(player.goals > 0 || player.assists > 0) && (
            <span className="ml-2 text-[#7C2C3B]">
              {player.goals > 0 && `${player.goals}G`}
              {player.goals > 0 && player.assists > 0 && ' '}
              {player.assists > 0 && `${player.assists}A`}
            </span>
          )}
        </div>
      </div>
      {showMinutes && (
        <div className="text-right w-12">
          <div className="text-xs text-slate-500">
            {player.minutes_played > 0 ? `${player.minutes_played}'` : '—'}
          </div>
        </div>
      )}
      {showGames && (
        <div className="text-right w-16">
          <div className="text-xs text-slate-500">
            {player.games_played > 0 ? `${player.games_played} gms` : '—'}
          </div>
        </div>
      )}
      <div className={`${getRatingColor(player.rating)} text-white font-bold text-sm px-2 py-1 rounded min-w-[40px] text-center`}>
        {player.rating !== null && player.rating !== undefined && typeof player.rating === 'number' ? player.rating.toFixed(1) : '-'}
      </div>
    </div>
  );
};

const StatsTable = ({ players, statType, title, icon: Icon }) => (
  <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
    <div className="bg-gradient-to-r from-[#7C2C3B] to-[#63232F] p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
            {title}
          </h3>
          <p className="text-white/70 text-sm">Top 100 all-time</p>
        </div>
      </div>
    </div>
    <div className="p-4 max-h-[600px] overflow-y-auto">
      {players.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          No data available
        </div>
      ) : (
        <div className="space-y-1">
          {players.map((player, idx) => (
            <StatRow 
              key={player.name} 
              rank={idx + 1} 
              player={player} 
              statType={statType}
            />
          ))}
        </div>
      )}
    </div>
  </div>
);

const RatingsTable = ({ data, title, subtitle, icon: Icon, showMinutes = false, showGames = false }) => (
  <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
    <div className="bg-gradient-to-r from-[#2BAEE5] to-[#1E9AD6] p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>
            {title}
          </h3>
          <p className="text-white/70 text-xs">{subtitle}</p>
        </div>
      </div>
    </div>
    <div className="p-3 max-h-[400px] overflow-y-auto">
      {!data || data.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          No ratings available
        </div>
      ) : (
        <div className="space-y-1">
          {[...data].filter(p => p && p.name).sort((a, b) => (b.rating || 0) - (a.rating || 0)).map((player, idx) => (
            <RatingRow 
              key={player.name} 
              rank={idx + 1} 
              player={player}
              showMinutes={showMinutes}
              showGames={showGames}
            />
          ))}
        </div>
      )}
    </div>
  </div>
);

const MatchInfoBadge = ({ matchInfo }) => {
  if (!matchInfo) return null;
  const result = matchInfo.result || '';
  const parts = result.split('-');
  const isWin = result && parts.length === 2 && (
    (matchInfo.venue?.startsWith('Home') && parseInt(parts[0]) > parseInt(parts[1])) ||
    (matchInfo.venue?.startsWith('Away') && parseInt(parts[1]) > parseInt(parts[0]))
  );
  const isDraw = result && parts.length === 2 && parts[0] === parts[1];
  
  return (
    <div className="bg-slate-100 rounded-lg p-3 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="text-sm font-semibold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
            {matchInfo.venue?.startsWith('Home') ? 'West Ham' : matchInfo.opponent} vs {matchInfo.venue?.startsWith('Home') ? matchInfo.opponent : 'West Ham'}
          </div>
          <div className="text-xs text-slate-500">{matchInfo.competition} • {matchInfo.date}</div>
          {(matchInfo.scorer || matchInfo.scorers) && (
            <div className="text-xs text-[#7C2C3B] mt-1">⚽ {matchInfo.scorer || matchInfo.scorers}</div>
          )}
        </div>
        <div className={`text-xl font-bold ${isWin ? 'text-green-600' : isDraw ? 'text-slate-600' : 'text-red-600'}`} style={{ fontFamily: 'Barlow Condensed' }}>
          {matchInfo.result}
        </div>
      </div>
    </div>
  );
};

// Head-to-Head Section Component
const HeadToHeadSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOpponent, setSelectedOpponent] = useState(null);
  const [opponentDetails, setOpponentDetails] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("played");
  const [showEuropean, setShowEuropean] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/head-to-head`);
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch head-to-head data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOpponentDetails = async (opponentId) => {
    try {
      const response = await axios.get(`${API}/head-to-head/${opponentId}`);
      setOpponentDetails(response.data);
      setSelectedOpponent(opponentId);
    } catch (error) {
      console.error("Failed to fetch opponent details:", error);
    }
  };

  const getSortedOpponents = () => {
    if (!data) return [];
    
    const opponents = showEuropean 
      ? data.categories.european 
      : data.categories.domestic;
    
    let filtered = searchQuery 
      ? opponents.filter(o => o.name.toLowerCase().includes(searchQuery.toLowerCase()))
      : opponents;
    
    return [...filtered].sort((a, b) => {
      switch(sortBy) {
        case "name": return a.name.localeCompare(b.name);
        case "played": return b.played - a.played;
        case "wins": return b.won - a.won;
        case "win_pct": return b.win_percentage - a.win_percentage;
        default: return b.played - a.played;
      }
    });
  };

  const getResultColor = (won, drawn, lost) => {
    const total = won + drawn + lost;
    if (total === 0) return 'bg-slate-100';
    const winPct = (won / total) * 100;
    if (winPct >= 50) return 'bg-green-100 text-green-800';
    if (winPct >= 30) return 'bg-amber-100 text-amber-800';
    return 'bg-red-100 text-red-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="head-to-head-section">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
          <div className="text-3xl font-bold text-[#7C2C3B]" style={{ fontFamily: 'Barlow Condensed' }}>
            {data?.total_opponents}
          </div>
          <div className="text-sm text-slate-500">Opponents</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
          <div className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
            {data?.total_matches?.toLocaleString()}
          </div>
          <div className="text-sm text-slate-500">Matches</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
          <div className="text-3xl font-bold text-green-600" style={{ fontFamily: 'Barlow Condensed' }}>
            {data?.total_record?.won}
          </div>
          <div className="text-sm text-slate-500">Wins</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
          <div className="text-3xl font-bold text-[#2BAEE5]" style={{ fontFamily: 'Barlow Condensed' }}>
            {data?.total_record?.win_percentage}%
          </div>
          <div className="text-sm text-slate-500">Win Rate</div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Opponents List */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center justify-between gap-4 mb-3">
              <h3 className="font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
                {showEuropean ? 'EUROPEAN OPPONENTS' : 'DOMESTIC OPPONENTS'}
              </h3>
              <button
                onClick={() => setShowEuropean(!showEuropean)}
                className="text-xs text-[#2BAEE5] hover:underline"
              >
                {showEuropean ? 'Show Domestic' : 'Show European'}
              </button>
            </div>
            
            {/* Search and Sort */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search opponents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9"
                  data-testid="h2h-search"
                />
              </div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="h-9 px-3 border rounded-md text-sm bg-white"
                data-testid="h2h-sort"
              >
                <option value="played">Most Played</option>
                <option value="wins">Most Wins</option>
                <option value="win_pct">Win %</option>
                <option value="name">A-Z</option>
              </select>
            </div>
          </div>
          
          {/* Opponents List */}
          <div className="max-h-[500px] overflow-y-auto">
            {getSortedOpponents().map((opponent) => (
              <button
                key={opponent.id}
                onClick={() => fetchOpponentDetails(opponent.id)}
                className={`w-full p-3 flex items-center justify-between hover:bg-slate-50 transition-colors border-b border-slate-100 ${selectedOpponent === opponent.id ? 'bg-[#7C2C3B]/5 border-l-4 border-l-[#7C2C3B]' : ''}`}
                data-testid={`h2h-opponent-${opponent.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold ${getResultColor(opponent.won, opponent.drawn, opponent.lost)}`}>
                    {opponent.win_percentage}%
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
                      {opponent.name.toUpperCase()}
                    </div>
                    <div className="text-xs text-slate-500">
                      P{opponent.played} W{opponent.won} D{opponent.drawn} L{opponent.lost}
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
            ))}
          </div>
        </div>

        {/* Opponent Details */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {opponentDetails ? (
            <>
              <div className="p-4 bg-gradient-to-r from-[#7C2C3B] to-[#63232F] text-white">
                <h3 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                  VS {opponentDetails.opponent.toUpperCase()}
                </h3>
                <p className="text-white/70 text-sm">{opponentDetails.competition}</p>
              </div>
              
              {/* Record Summary */}
              <div className="p-4 border-b border-slate-200">
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div>
                    <div className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
                      {opponentDetails.played}
                    </div>
                    <div className="text-xs text-slate-500">Played</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600" style={{ fontFamily: 'Barlow Condensed' }}>
                      {opponentDetails.won}
                    </div>
                    <div className="text-xs text-slate-500">Won</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-500" style={{ fontFamily: 'Barlow Condensed' }}>
                      {opponentDetails.drawn}
                    </div>
                    <div className="text-xs text-slate-500">Drawn</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Barlow Condensed' }}>
                      {opponentDetails.lost}
                    </div>
                    <div className="text-xs text-slate-500">Lost</div>
                  </div>
                </div>
                
                {/* Win/Draw/Loss Bar */}
                <div className="mt-3 h-3 rounded-full overflow-hidden flex bg-slate-200">
                  <div 
                    className="bg-green-500 transition-all" 
                    style={{ width: `${(opponentDetails.won / opponentDetails.played) * 100}%` }}
                  />
                  <div 
                    className="bg-slate-400 transition-all" 
                    style={{ width: `${(opponentDetails.drawn / opponentDetails.played) * 100}%` }}
                  />
                  <div 
                    className="bg-red-500 transition-all" 
                    style={{ width: `${(opponentDetails.lost / opponentDetails.played) * 100}%` }}
                  />
                </div>
              </div>
              
              {/* Goals */}
              <div className="p-4 border-b border-slate-200">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-xl font-bold text-[#7C2C3B]" style={{ fontFamily: 'Barlow Condensed' }}>
                      {opponentDetails.goals_for}
                    </div>
                    <div className="text-xs text-slate-500">Goals For</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-slate-600" style={{ fontFamily: 'Barlow Condensed' }}>
                      {opponentDetails.goal_difference > 0 ? '+' : ''}{opponentDetails.goal_difference}
                    </div>
                    <div className="text-xs text-slate-500">GD</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-slate-500" style={{ fontFamily: 'Barlow Condensed' }}>
                      {opponentDetails.goals_against}
                    </div>
                    <div className="text-xs text-slate-500">Goals Against</div>
                  </div>
                </div>
              </div>
              
              {/* Home/Away Record */}
              <div className="p-4 border-b border-slate-200">
                <h4 className="font-bold text-sm text-slate-600 mb-3">HOME & AWAY</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <div className="text-xs text-slate-500 mb-1">Home</div>
                    <div className="text-sm font-semibold">
                      P{opponentDetails.home?.played} W{opponentDetails.home?.won} D{opponentDetails.home?.drawn} L{opponentDetails.home?.lost}
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <div className="text-xs text-slate-500 mb-1">Away</div>
                    <div className="text-sm font-semibold">
                      P{opponentDetails.away?.played} W{opponentDetails.away?.won} D{opponentDetails.away?.drawn} L{opponentDetails.away?.lost}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Biggest Results */}
              <div className="p-4 border-b border-slate-200">
                <h4 className="font-bold text-sm text-slate-600 mb-3">MEMORABLE RESULTS</h4>
                <div className="space-y-2">
                  {opponentDetails.biggest_win && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="w-20 text-slate-500">Biggest Win:</span>
                      <span className="font-semibold text-green-600">{opponentDetails.biggest_win}</span>
                    </div>
                  )}
                  {opponentDetails.biggest_loss && opponentDetails.biggest_loss !== "N/A" && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="w-20 text-slate-500">Biggest Loss:</span>
                      <span className="font-semibold text-red-600">{opponentDetails.biggest_loss}</span>
                    </div>
                  )}
                  {opponentDetails.first_meeting && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="w-20 text-slate-500">First Met:</span>
                      <span className="font-semibold">{opponentDetails.first_meeting}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Notable Matches */}
              {opponentDetails.notable_matches?.length > 0 && (
                <div className="p-4">
                  <h4 className="font-bold text-sm text-slate-600 mb-3">HISTORIC MATCHES</h4>
                  <div className="space-y-2">
                    {opponentDetails.notable_matches.map((match, idx) => (
                      <div key={idx} className="bg-[#F8D742]/10 rounded-lg p-3 border border-[#F8D742]/30">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-[#7C2C3B] font-semibold">{match.competition}</span>
                          <span className="text-xs text-slate-500">{match.date}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-lg text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
                            {match.result}
                          </span>
                          <span className="text-xs text-slate-500">({match.venue})</span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">{match.note}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center text-slate-400">
              <Swords className="w-12 h-12 mb-4 opacity-30" />
              <p className="text-lg font-medium">Select an opponent</p>
              <p className="text-sm">Click on a team to see the full head-to-head record</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function TopStatsPage() {
  const [stats, setStats] = useState(null);
  const [ratings, setRatings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState("mens");
  const [activeTab, setActiveTab] = useState("all-time");
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, ratingsRes, autoMenRes, autoWomenRes] = await Promise.all([
        axios.get(`${API}/players/top-stats`).catch(() => ({ data: { mens: { top_scorers: [], top_appearances: [] }, womens: { top_scorers: [], top_appearances: [] } } })),
        axios.get(`${API}/players/ratings`).catch(() => ({ data: { mens: {}, womens: {} } })),
        axios.get(`${API}/player-ratings/auto/men`).catch(() => ({ data: null })),
        axios.get(`${API}/player-ratings/auto/women`).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data || { mens: { top_scorers: [], top_appearances: [] }, womens: { top_scorers: [], top_appearances: [] } });

      const oldRatings = ratingsRes.data || { mens: {}, womens: {} };
      const autoMen = autoMenRes.data;
      const autoWomen = autoWomenRes.data;

      // Merge auto-scraped data with existing ratings
      const merged = { ...oldRatings };

      // Men's season average from FotMob
      if (autoMen?.season_ratings?.length > 0) {
        if (!merged.mens) merged.mens = {};
        merged.mens.season_average = {
          season: "2025-26",
          competition: "Championship",
          source: "FotMob",
          ratings: autoMen.season_ratings.map(p => ({
            name: p.name,
            rating: p.season_rating,
            games_played: p.appearances,
            goals: p.goals || 0,
            assists: p.assists || 0,
            minutes_played: p.mins_played || p.minutes_played || 0,
            position: "Player",
          })),
        };
      }

      // Men's last match lineup from FotMob
      if (autoMen?.last_match?.starters?.length > 0) {
        if (!merged.mens) merged.mens = {};
        const lm = autoMen.last_match;
        const seasonMap = {};
        (autoMen.season_ratings || []).forEach(p => { seasonMap[p.name] = p.season_rating; });
        
        const allPlayers = [
          ...lm.starters.map((p, i) => ({ ...p, order: i, role: "starter" })),
          ...lm.subs.map((p, i) => ({ ...p, order: 100 + i, role: "sub" })),
        ];

        // Use auto-scraped last match if it's more recent than admin ratings
        const adminDate = merged.mens?.last_game?.match_info?.date || "";
        const autoDate = autoMen.scraped_at ? new Date(autoMen.scraped_at).toISOString().slice(0, 10) : "";
        const adminDateParsed = adminDate ? new Date(adminDate.split('/').reverse().join('-') || adminDate) : new Date(0);
        const autoDateParsed = autoDate ? new Date(autoDate) : new Date(0);
        const hasAdminRatings = merged.mens?.last_game?.ratings?.some(r => r.rating > 0);
        
        // Prefer auto if: admin has no real ratings, OR auto is more recent
        if (!hasAdminRatings || autoDateParsed > adminDateParsed) {
          merged.mens.last_game = {
            match_info: {
              opponent: lm.opponent || lm.away_team || "",
              result: lm.result || "",
              venue: lm.home_team === "West Ham United" ? "Home" : "Away",
              competition: "Championship",
              date: autoMen.scraped_at ? new Date(autoMen.scraped_at).toLocaleDateString("en-GB") : "",
              team_rating: lm.team_rating,
              formation: lm.formation,
            },
            ratings: allPlayers.map(p => ({
              name: p.name,
              rating: seasonMap[p.name] || 0,
              position: p.role === "starter" ? "Starter" : "Sub",
              minutes_played: p.role === "starter" ? 90 : 0,
            })),
          };
        }
      }

      // Women's season average from FotMob
      if (autoWomen?.season_ratings?.length > 0) {
        if (!merged.womens) merged.womens = {};
        merged.womens.season_average = {
          season: "2025-26",
          competition: "WSL",
          source: "FotMob",
          ratings: autoWomen.season_ratings.map(p => ({
            name: p.name,
            rating: p.season_rating,
            games_played: p.appearances,
            goals: p.goals || 0,
            assists: p.assists || 0,
            minutes_played: p.mins_played || p.minutes_played || 0,
            position: "Player",
          })),
        };
      }

      // Women's last match lineup
      if (autoWomen?.last_match?.starters?.length > 0) {
        if (!merged.womens) merged.womens = {};
        const lm = autoWomen.last_match;
        const seasonMap = {};
        (autoWomen.season_ratings || []).forEach(p => { seasonMap[p.name] = p.season_rating; });
        
        const allPlayers = [
          ...lm.starters.map((p, i) => ({ ...p, order: i, role: "starter" })),
          ...lm.subs.map((p, i) => ({ ...p, order: 100 + i, role: "sub" })),
        ];

        const adminDate = merged.womens?.last_game?.match_info?.date || "";
        const autoDate = autoWomen.scraped_at ? new Date(autoWomen.scraped_at).toISOString().slice(0, 10) : "";
        const adminDateParsed = adminDate ? new Date(adminDate.split('/').reverse().join('-') || adminDate) : new Date(0);
        const autoDateParsed = autoDate ? new Date(autoDate) : new Date(0);
        const hasAdminRatings = merged.womens?.last_game?.ratings?.some(r => r.rating > 0);
        
        if (!hasAdminRatings || autoDateParsed > adminDateParsed) {
          merged.womens.last_game = {
            match_info: {
              opponent: lm.opponent || lm.away_team || "",
              result: lm.result || "",
              venue: lm.home_team === "West Ham United" ? "Home" : "Away",
              competition: "WSL",
              date: autoWomen.scraped_at ? new Date(autoWomen.scraped_at).toLocaleDateString("en-GB") : "",
              team_rating: lm.team_rating,
              formation: lm.formation,
            },
            ratings: allPlayers.map(p => ({
              name: p.name,
              rating: seasonMap[p.name] || 0,
              position: p.role === "starter" ? "Starter" : "Sub",
              minutes_played: p.role === "starter" ? 90 : 0,
            })),
          };
        }
      }

      setRatings(merged);

      // If data is empty (backend scrapers still running), auto-retry up to 8 times
      const hasRatings = merged?.mens?.last_game?.ratings?.length > 0 || merged?.mens?.season_average?.ratings?.length > 0;
      const hasStats = (statsRes.data?.mens?.top_scorers?.length > 0) || (statsRes.data?.womens?.top_scorers?.length > 0);
      if ((!hasRatings || !hasStats) && retryCount < 8) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          fetchData();
        }, 5000);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      setStats({ mens: { top_scorers: [], top_appearances: [] }, womens: { top_scorers: [], top_appearances: [] } });
      setRatings({ mens: {}, womens: {} });
    } finally {
      setLoading(false);
    }
  };

  const currentStats = selectedTeam === "mens" ? stats?.mens : stats?.womens;
  const currentRatings = selectedTeam === "mens" ? ratings?.mens : ratings?.womens;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="top-stats-page">
      <SEO title="Top Stats & Player Ratings" path="/stats" description="West Ham United all-time top scorers, most appearances, season player ratings and head-to-head records. FotMob-powered live ratings for men's and women's teams." />
      <Navbar />
      
      {/* Header */}
      <div className="bg-[#7C2C3B] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            TOP STATS
          </h1>
          <p className="text-white/80">All-time records, player ratings and performance stats</p>
        </div>
      </div>

      {/* Team Selector */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <Tabs value={selectedTeam} onValueChange={setSelectedTeam} className="w-full sm:w-auto">
              <TabsList className="grid w-full sm:w-auto grid-cols-2">
                <TabsTrigger value="mens" data-testid="tab-mens" className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Men's First Team
                </TabsTrigger>
                <TabsTrigger value="womens" data-testid="tab-womens" className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Women's First Team
                </TabsTrigger>
              </TabsList>
            </Tabs>
            
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full sm:w-auto">
              <TabsList className="grid w-full sm:w-auto grid-cols-3">
                <TabsTrigger value="all-time" className="flex items-center gap-2">
                  <Trophy className="w-4 h-4" />
                  All-Time
                </TabsTrigger>
                <TabsTrigger value="head-to-head" data-testid="tab-head-to-head" className="flex items-center gap-2">
                  <Swords className="w-4 h-4" />
                  H2H
                </TabsTrigger>
                <TabsTrigger value="ratings" className="flex items-center gap-2">
                  <Star className="w-4 h-4" />
                  Ratings
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading && activeTab !== "head-to-head" ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : activeTab === "all-time" ? (
          <div className="grid lg:grid-cols-2 gap-8">
            <StatsTable 
              players={currentStats?.top_scorers || []}
              statType="goals"
              title="TOP GOAL SCORERS"
              icon={Target}
            />
            <StatsTable 
              players={currentStats?.top_appearances || []}
              statType="appearances"
              title="MOST APPEARANCES"
              icon={Award}
            />
          </div>
        ) : activeTab === "head-to-head" ? (
          <HeadToHeadSection />
        ) : (
          <div className="space-y-8">
            {/* Player Ratings Section */}
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Last Game Ratings */}
              <div>
                <h2 className="text-2xl font-bold text-[#0F172A] mb-4 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  <Calendar className="w-6 h-6 text-[#7C2C3B]" />
                  LAST GAME RATINGS
                </h2>
                {currentRatings?.last_game?.match_info && (
                  <MatchInfoBadge matchInfo={currentRatings.last_game.match_info} />
                )}
                <RatingsTable 
                  data={currentRatings?.last_game?.ratings || []}
                  title="PLAYER RATINGS"
                  subtitle={`${currentRatings?.last_game?.match_info?.opponent || 'Last Match'}`}
                  icon={Star}
                  showMinutes={true}
                />
              </div>
              
              {/* Season Average Ratings */}
              <div>
                <h2 className="text-2xl font-bold text-[#0F172A] mb-4 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  <TrendingUp className="w-6 h-6 text-[#2BAEE5]" />
                  SEASON AVERAGE RATINGS
                </h2>
                <div className="bg-slate-100 rounded-lg p-3 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
                        {currentRatings?.season_average?.season || '2025-26'} {currentRatings?.season_average?.competition || 'Season'}
                      </div>
                      <div className="text-xs text-slate-500">Average rating across all league appearances</div>
                    </div>
                  </div>
                </div>
                <RatingsTable 
                  data={currentRatings?.season_average?.ratings || []}
                  title="SEASON AVERAGES"
                  subtitle={currentRatings?.season_average?.competition || '2025-26'}
                  icon={TrendingUp}
                  showGames={true}
                  showMinutes={true}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
